import _extends from "@babel/runtime/helpers/extends";
import classnames from "classnames/dedupe";
import * as PropTypes from "prop-types";
import React, { forwardRef, useEffect, useState } from "react";
import { HelperText } from "../helper-text";
import { Select } from "../select";
import { TextInput } from "../text-input";

const getCountryCode = (phone = "", countryCodes = []) => {
  return countryCodes.sort((a, b) => a < b ? 1 : b < a ? -1 : 0).find(code => phone.startsWith(code)) || "+1";
};

const getDigits = (phone = "", countryCodes = []) => {
  if (!phone.startsWith("+")) {
    return phone;
  }

  const countryCode = countryCodes.sort((a, b) => a < b ? 1 : b < a ? -1 : 0).find(code => phone.startsWith(code));
  return countryCode ? phone.replace(countryCode, "") : phone;
};

export const PhoneInput = /*#__PURE__*/forwardRef(
/**
 * PhoneInput component
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {string} props.className
 * @param {string} props.errorText
 * @param {string} [props.value]
 * @param {boolean} [props.disabled]
 * @param {string[]} [props.countryCodes]
 * @param {(phoneNumber: string) => void} [props.onChange]
 * @param {*} ref
 * @returns {JSX.Element}
 */
function PhoneInput({
  className,
  errorText,
  value,
  disabled,
  countryCodes = ["+1"],
  onChange = () => {},
  ...props
}, ref) {
  const phoneClasses = classnames("flex", className);
  const [countryCode, setCountryCode] = useState(value ? getCountryCode(value, countryCodes) : "+1");
  const [digits, setDigits] = useState(value ? getDigits(value, countryCodes) : "");
  const [phoneNumber, setPhoneNumber] = useState(`${countryCode}${digits}`);

  const onCountryCodeChange = e => {
    setCountryCode(e?.target?.value);
    onPhoneNumberChange(e?.target?.value, digits);
  };

  const onDigitsChange = e => {
    setDigits(e?.target?.value);
    onPhoneNumberChange(countryCode, e?.target?.value);
  };

  const onPhoneNumberChange = (countryCode, digits) => {
    const value = digits ? `${countryCode}${digits}` : null;
    setPhoneNumber(value);
    onChange(value);
  };

  useEffect(() => {
    setCountryCode(value ? getCountryCode(value, countryCodes) : "+1");
    setDigits(value ? getDigits(value, countryCodes) : "");
  }, [value]);
  return /*#__PURE__*/React.createElement("div", {
    className: phoneClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-1/4 pr-1"
  }, /*#__PURE__*/React.createElement(Select, {
    name: "phone_country_code",
    className: "w-full",
    value: countryCode,
    onChange: onCountryCodeChange,
    disabled: disabled
  }, countryCodes.map(code => /*#__PURE__*/React.createElement("option", {
    value: code,
    key: code
  }, code)))), /*#__PURE__*/React.createElement("div", {
    className: "w-3/4 pl-1"
  }, /*#__PURE__*/React.createElement(TextInput, {
    name: "phone_digits",
    className: "w-full",
    placeholder: "Phone Number",
    valid: disabled ? undefined : !errorText // disabled classes aren't added when 'valid' is different from undefined or null
    ,
    value: digits,
    disabled: disabled,
    onChange: onDigitsChange
  }), /*#__PURE__*/React.createElement("div", {
    className: "text-left"
  }, errorText ? /*#__PURE__*/React.createElement(HelperText, {
    valid: false
  }, errorText) : null)), /*#__PURE__*/React.createElement("div", {
    className: "hidden"
  }, /*#__PURE__*/React.createElement("input", _extends({
    name: "phone_line_number",
    type: "hidden",
    value: digits ? phoneNumber : null,
    onChange: onPhoneNumberChange,
    ref: ref
  }, props))));
});
PhoneInput.propTypes = {
  className: PropTypes.string,
  errorText: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  countryCodes: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func
};